/**
 * Быстрые шаблоны ответа в «Чатах». `TemplateButton` — кнопка у «Отправить»: список шаблонов по
 * категориям с поиском (набираешь — остаются подходящие, Enter — первый), клик кладёт текст в
 * поле ответа. `TemplateManager` — окно управления: категории (создать, переименовать, порядок),
 * шаблоны (добавить, изменить, перетащить в другую категорию или на другое место), архив вместо
 * удаления и базовый набор. Категории — общие (компании) и личные («Мои», видит только владелец).
 * Спека
 * `docs/specs/2026-10-02-chat-templates-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArchiveRestore,
  ArrowDown,
  ArrowUp,
  GripVertical,
  MessageSquareText,
  Pencil,
  Plus,
  Search,
  Settings2,
  X,
} from "lucide-react";
import { type DragEvent, useEffect, useRef, useState } from "react";

import {
  archiveCategory,
  archiveTemplate,
  createCategory,
  createTemplate,
  editTemplate,
  findTemplates,
  loadTemplates,
  orderCategories,
  orderTemplates,
  renameCategory,
  restoreCategory,
  restoreTemplate,
  seedTemplates,
  type Template,
  type TemplateCategory,
  type TemplateTree,
} from "@/lib/chatTemplates";

const KEY = ["chat-templates"];

export function TemplateButton({ onPick }: { onPick: (text: string) => void }) {
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState(false);
  const [q, setQ] = useState("");
  // Без запроса категории свёрнуты (их десятки): раскрыта та, по которой кликнули.
  const [opened, setOpened] = useState<number | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const tree = useQuery({ queryKey: KEY, queryFn: loadTemplates, enabled: open || manage });
  const found = findTemplates(tree.data?.categories ?? [], q);
  const searching = q.trim() !== "";
  const few = found.reduce((n, c) => n + c.templates.length, 0) <= 12;
  const empty = tree.data && tree.data.categories.every((c) => c.templates.length === 0);

  // Клик мимо списка закрывает его.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  const pick = (t: Template) => {
    onPick(t.text);
    setOpen(false);
    setQ("");
  };

  return (
    <div ref={box} className="relative flex-none">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Шаблоны ответа"
        aria-expanded={open}
        className={`grid size-12 place-items-center rounded-full bg-[var(--tg-surface)] shadow-[0_1px_8px_1px_rgba(0,0,0,.12)] hover:bg-[var(--tg-hover)] ${
          open ? "text-[var(--tg-primary)]" : "text-[var(--tg-sec)]"
        }`}
        data-annot="шаблоны ответа"
      >
        <MessageSquareText size={22} />
      </button>
      {open && (
        <div
          className="absolute bottom-14 right-0 z-30 flex max-h-[min(520px,70vh)] w-[min(420px,calc(100vw-16px))] flex-col overflow-hidden rounded-[16px] bg-[var(--tg-surface)] text-[14px] shadow-[0_4px_24px_rgba(0,0,0,.28)]"
          data-annot="список шаблонов"
        >
          <div className="flex flex-none items-center gap-2 border-b border-black/10 px-3 py-2">
            <Search size={16} className="flex-none text-[var(--tg-sec)]" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setOpen(false);
                if (e.key !== "Enter") return;
                e.preventDefault(); // иначе Enter долетит до поля ответа, куда уходит фокус
                const first = found[0]?.templates[0];
                if (searching && first) pick(first);
              }}
              placeholder="Поиск шаблона"
              aria-label="Поиск шаблона"
              className="h-8 min-w-0 flex-1 bg-transparent outline-none placeholder:text-[var(--tg-sec)]"
            />
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setManage(true);
              }}
              title="Управление шаблонами: добавить, изменить, переставить"
              className="grid size-8 flex-none place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]"
              data-annot="управление шаблонами"
            >
              <Settings2 size={18} />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto py-1">
            {tree.isPending && <p className="px-3 py-2 text-[var(--tg-sec)]">Загрузка…</p>}
            {tree.isError && <p className="px-3 py-2 text-[#e53935]">{tree.error.message}</p>}
            {empty && (
              <p className="px-3 py-2 text-[var(--tg-sec)]">
                Шаблонов пока нет — добавьте их в управлении (кнопка справа от поиска).
              </p>
            )}
            {tree.data && !empty && found.length === 0 && (
              <p className="px-3 py-2 text-[var(--tg-sec)]">Под «{q}» ничего не подходит</p>
            )}
            {found.map((c, ci) => (
              <div key={c.id}>
                <button
                  type="button"
                  onClick={() => setOpened(opened === c.id ? null : c.id)}
                  disabled={searching || few}
                  className="flex w-full items-center gap-1.5 px-3 pb-0.5 pt-2 text-left text-[12px] font-medium uppercase tracking-wide text-[var(--tg-sec)] enabled:hover:text-[var(--tg-primary)]"
                >
                  <span className="min-w-0 truncate">{c.name}</span>
                  {c.personal && <span className="flex-none normal-case text-[var(--tg-primary)]">мой</span>}
                  <span className="ml-auto flex-none normal-case">{c.templates.length}</span>
                </button>
                {(searching || few || opened === c.id) && c.templates.map((t, ti) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => pick(t)}
                    title={t.text}
                    className={`block w-full px-3 py-1.5 text-left hover:bg-[var(--tg-hover)] ${
                      searching && ci === 0 && ti === 0 ? "bg-[var(--tg-hover)]" : ""
                    }`}
                  >
                    <div className="truncate font-medium">{t.title}</div>
                    <div className="line-clamp-2 text-[13px] text-[var(--tg-sec)]">{t.text}</div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      {manage && <TemplateManager onClose={() => setManage(false)} />}
    </div>
  );
}

/** Что тащат: шаблон или категорию. */
type Dragged = { kind: "template" | "category"; id: number };
/** Форма шаблона: `id` — правка существующего, иначе новый в категории. */
type Draft = { id: number | null; category_id: number; title: string; text: string };

function TemplateManager({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const tree = useQuery({ queryKey: KEY, queryFn: loadTemplates });
  const run = useMutation({
    mutationFn: (job: () => Promise<TemplateTree>) => job(),
    onSuccess: (data) => qc.setQueryData(KEY, data),
    onError: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
  const [dragged, setDragged] = useState<Dragged | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [renaming, setRenaming] = useState<{ id: number; name: string } | null>(null);
  const [fresh, setFresh] = useState("");
  const [archive, setArchive] = useState(false);
  const [q, setQ] = useState("");
  // Раздел: общие шаблоны компании или личные сотрудника.
  const [personal, setPersonal] = useState(false);

  const every = tree.data?.categories ?? [];
  const categories = every.filter((c) => c.personal === personal);
  const shown = q.trim() ? findTemplates(categories, q) : categories;
  const gone = tree.data && {
    categories: tree.data.archive.categories.filter((c) => c.personal === personal),
    templates: tree.data.archive.templates.filter((t) => t.personal === personal),
  };
  const goneCount = (gone?.categories.length ?? 0) + (gone?.templates.length ?? 0);

  const moveCategory = (id: number, before: number | null) => {
    const ids = categories.map((c) => c.id).filter((x) => x !== id);
    ids.splice(before === null ? ids.length : ids.indexOf(before), 0, id);
    run.mutate(() => orderCategories(ids, personal));
  };
  const shiftCategory = (id: number, step: -1 | 1) => {
    const ids = categories.map((c) => c.id);
    const i = ids.indexOf(id);
    const [a, b] = [ids[i], ids[i + step]];
    if (i < 0 || a === undefined || b === undefined) return;
    [ids[i], ids[i + step]] = [b, a];
    run.mutate(() => orderCategories(ids, personal));
  };
  /** Шаблон — в категорию `target` перед шаблоном `before` (null — в конец). */
  const moveTemplate = (id: number, target: TemplateCategory, before: number | null) => {
    if (id === before) return;
    const ids = target.templates.map((t) => t.id).filter((x) => x !== id);
    ids.splice(before === null ? ids.length : ids.indexOf(before), 0, id);
    run.mutate(() => orderTemplates(target.id, ids));
  };
  const drop = (e: DragEvent, target: TemplateCategory, before: number | null) => {
    e.preventDefault();
    e.stopPropagation();
    setOver(null);
    if (!dragged) return;
    if (dragged.kind === "template") moveTemplate(dragged.id, target, before);
    else if (dragged.id !== target.id) moveCategory(dragged.id, target.id);
    setDragged(null);
  };
  const allow = (e: DragEvent, mark: string) => {
    if (!dragged) return;
    e.preventDefault();
    e.stopPropagation();
    setOver(mark);
  };
  const save = () => {
    if (!draft || !draft.text.trim()) return;
    const { id, category_id, title, text } = draft;
    run.mutate(async () => {
      if (id === null) return createTemplate(category_id, title, text);
      const data = await editTemplate(id, title, text);
      const now = data.categories.find((c) => c.templates.some((t) => t.id === id));
      // Категорию в форме сменили — шаблон переезжает в конец новой.
      const target = data.categories.find((c) => c.id === category_id);
      return now && target && now.id !== target.id
        ? orderTemplates(target.id, [...target.templates.map((t) => t.id), id])
        : data;
    });
    setDraft(null);
  };

  const icon = "grid size-7 flex-none place-items-center rounded text-fg-faint hover:bg-bg-hover hover:text-fg disabled:opacity-40";
  const input = "h-8 min-w-0 rounded-[var(--radius-ui)] border border-border bg-transparent px-2 text-[13px]";

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/60 sm:p-4" onClick={onClose}>
      <div
        className="flex w-full max-w-[860px] flex-col overflow-hidden bg-bg text-[13.5px] text-fg sm:rounded-[16px]"
        onClick={(e) => e.stopPropagation()}
        data-annot="управление шаблонами"
      >
        <div className="flex flex-none flex-wrap items-center gap-2 border-b border-border px-4 py-2.5">
          <div className="text-[15px] font-medium">Шаблоны ответа</div>
          <div className="flex rounded-[var(--radius-ui)] border border-border p-0.5" data-annot="общие или мои шаблоны">
            {[false, true].map((mine) => (
              <button
                key={String(mine)}
                type="button"
                onClick={() => (setPersonal(mine), setDraft(null), setRenaming(null))}
                title={mine ? "Личные шаблоны: видите и правите только вы" : "Шаблоны компании: видят и правят все"}
                className={`h-7 rounded px-2.5 ${personal === mine ? "bg-accent text-white" : "text-fg-muted hover:bg-bg-hover"}`}
              >
                {mine ? "Мои" : "Общие"} · {every.filter((c) => c.personal === mine).reduce((n, c) => n + c.templates.length, 0)}
              </button>
            ))}
          </div>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск"
            aria-label="Поиск шаблона"
            className={`${input} ml-2 w-40 flex-1 sm:flex-none`}
          />
          {!personal && <button
            type="button"
            onClick={() => run.mutate(seedTemplates)}
            disabled={run.isPending}
            title="Долить базовый набор: адреса салонов и ответы справочника КЦ. Уже имеющиеся и убранные в архив не трогает"
            className="ml-auto h-8 rounded-[var(--radius-ui)] border border-border px-2 text-fg-muted hover:bg-bg-hover disabled:opacity-50"
            data-annot="загрузить базовые шаблоны"
          >
            Загрузить базовые
          </button>}
          <button type="button" onClick={onClose} title="Закрыть" className={`${icon} ${personal ? "ml-auto" : ""}`}>
            <X size={16} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {tree.isPending && <p className="text-fg-muted">Загрузка…</p>}
          {(tree.error ?? run.error) && <p className="mb-2 text-err">{(tree.error ?? run.error)?.message}</p>}
          {tree.data && categories.length === 0 && (
            <p className="mb-3 text-fg-muted">
              {personal
                ? "Личных шаблонов пока нет: их видите только вы. Создайте категорию ниже."
                : "Шаблонов пока нет. Создайте категорию ниже или нажмите «Загрузить базовые»."}
            </p>
          )}
          {q.trim() && shown.length === 0 && <p className="text-fg-muted">Под «{q}» ничего не подходит</p>}
          {shown.map((c) => {
            const full = categories.find((x) => x.id === c.id) ?? c;
            return (
              <section
                key={c.id}
                onDragOver={(e) => allow(e, `c${c.id}`)}
                onDrop={(e) => drop(e, full, null)}
                className={`mb-3 rounded-[var(--radius-card)] border bg-bg-subtle ${
                  over === `c${c.id}` ? "border-accent" : "border-border"
                }`}
              >
                <header
                  draggable={!renaming}
                  onDragStart={() => setDragged({ kind: "category", id: c.id })}
                  onDragEnd={() => (setDragged(null), setOver(null))}
                  className="flex items-center gap-1 px-2 py-1.5"
                >
                  <GripVertical size={16} className="flex-none cursor-grab text-fg-faint" aria-hidden />
                  {renaming?.id === c.id ? (
                    <form
                      className="flex min-w-0 flex-1 gap-1"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (renaming.name.trim()) run.mutate(() => renameCategory(c.id, renaming.name));
                        setRenaming(null);
                      }}
                    >
                      <input
                        autoFocus
                        value={renaming.name}
                        maxLength={64}
                        onChange={(e) => setRenaming({ id: c.id, name: e.target.value })}
                        onKeyDown={(e) => e.key === "Escape" && setRenaming(null)}
                        aria-label="Название категории"
                        className={`${input} flex-1`}
                      />
                      <button type="submit" className="h-8 rounded-[var(--radius-ui)] border border-border px-2 hover:bg-bg-hover">
                        Сохранить
                      </button>
                    </form>
                  ) : (
                    <div className="min-w-0 flex-1 truncate font-medium">
                      {c.name} <span className="font-normal text-fg-faint">· {full.templates.length}</span>
                    </div>
                  )}
                  <button type="button" onClick={() => shiftCategory(c.id, -1)} title="Выше" className={icon} disabled={categories[0]?.id === c.id}>
                    <ArrowUp size={15} />
                  </button>
                  <button type="button" onClick={() => shiftCategory(c.id, 1)} title="Ниже" className={icon} disabled={categories.at(-1)?.id === c.id}>
                    <ArrowDown size={15} />
                  </button>
                  <button type="button" onClick={() => setRenaming({ id: c.id, name: c.name })} title="Переименовать категорию" className={icon}>
                    <Pencil size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDraft({ id: null, category_id: c.id, title: "", text: "" })}
                    title="Добавить шаблон в категорию"
                    className={icon}
                  >
                    <Plus size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => run.mutate(() => archiveCategory(c.id))}
                    title="Категорию с её шаблонами — в архив"
                    className={`${icon} hover:text-err`}
                  >
                    <Archive size={15} />
                  </button>
                </header>
                {c.templates.map((t) =>
                  draft?.id === t.id ? (
                    <Editor key={t.id} draft={draft} categories={every} onChange={setDraft} onSave={save} onCancel={() => setDraft(null)} />
                  ) : (
                    <div
                      key={t.id}
                      draggable
                      onDragStart={(e) => (e.stopPropagation(), setDragged({ kind: "template", id: t.id }))}
                      onDragEnd={() => (setDragged(null), setOver(null))}
                      onDragOver={(e) => allow(e, dragged?.kind === "template" ? `t${t.id}` : `c${c.id}`)}
                      onDrop={(e) => drop(e, full, t.id)}
                      className={`flex items-start gap-1 border-t px-2 py-1.5 ${
                        over === `t${t.id}` ? "border-t-2 border-accent" : "border-border"
                      } ${dragged?.kind === "template" && dragged.id === t.id ? "opacity-40" : ""}`}
                    >
                      <GripVertical size={16} className="mt-0.5 flex-none cursor-grab text-fg-faint" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{t.title}</div>
                        <div className="line-clamp-2 whitespace-pre-line text-[12.5px] text-fg-muted">{t.text}</div>
                      </div>
                      <button type="button" onClick={() => setDraft({ ...t })} title="Изменить шаблон" className={icon}>
                        <Pencil size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => run.mutate(() => archiveTemplate(t.id))}
                        title="Шаблон — в архив"
                        className={`${icon} hover:text-err`}
                      >
                        <Archive size={15} />
                      </button>
                    </div>
                  ),
                )}
                {draft && draft.id === null && draft.category_id === c.id && (
                  <Editor draft={draft} categories={every} onChange={setDraft} onSave={save} onCancel={() => setDraft(null)} />
                )}
                {full.templates.length === 0 && !draft && (
                  <p className="border-t border-border px-3 py-2 text-[12.5px] text-fg-faint">
                    Пусто — добавьте шаблон «+» или перетащите сюда из другой категории
                  </p>
                )}
              </section>
            );
          })}
          <form
            className="flex gap-1"
            onSubmit={(e) => {
              e.preventDefault();
              if (!fresh.trim()) return;
              run.mutate(() => createCategory(fresh, personal));
              setFresh("");
            }}
          >
            <input
              value={fresh}
              onChange={(e) => setFresh(e.target.value)}
              maxLength={64}
              placeholder={personal ? "Новая личная категория" : "Новая общая категория"}
              aria-label="Название новой категории"
              className={`${input} flex-1`}
            />
            <button
              type="submit"
              disabled={!fresh.trim() || run.isPending}
              className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-fg-muted hover:bg-bg-hover disabled:opacity-50"
            >
              Создать категорию
            </button>
          </form>
          {goneCount > 0 && (
            <div className="mt-4">
              <button type="button" onClick={() => setArchive((v) => !v)} className="text-fg-muted underline-offset-2 hover:underline">
                Архив · {goneCount} {archive ? "▴" : "▾"}
              </button>
              {archive && gone && (
                <div className="mt-2 rounded-[var(--radius-card)] border border-border">
                  {gone.categories.map((c) => (
                    <div key={`c${c.id}`} className="flex items-center gap-2 border-b border-border px-3 py-1.5 last:border-b-0">
                      <div className="min-w-0 flex-1 truncate">
                        Категория «{c.name}» <span className="text-fg-faint">· шаблонов: {c.templates}</span>
                      </div>
                      <button type="button" onClick={() => run.mutate(() => restoreCategory(c.id))} title="Вернуть категорию с шаблонами" className={icon}>
                        <ArchiveRestore size={15} />
                      </button>
                    </div>
                  ))}
                  {gone.templates.map((t) => (
                    <div key={`t${t.id}`} className="flex items-start gap-2 border-b border-border px-3 py-1.5 last:border-b-0">
                      <div className="min-w-0 flex-1">
                        <div className="truncate">
                          {t.title} <span className="text-fg-faint">· {t.category}</span>
                        </div>
                        <div className="line-clamp-1 text-[12.5px] text-fg-muted">{t.text}</div>
                      </div>
                      <button type="button" onClick={() => run.mutate(() => restoreTemplate(t.id))} title="Вернуть шаблон в его категорию" className={icon}>
                        <ArchiveRestore size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Editor({
  draft,
  categories,
  onChange,
  onSave,
  onCancel,
}: {
  draft: Draft;
  categories: TemplateCategory[];
  onChange: (d: Draft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const input = "min-w-0 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[13px]";
  return (
    <form
      className="flex flex-col gap-1.5 border-t border-border p-2"
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
    >
      <div className="flex flex-wrap gap-1.5">
        <input
          autoFocus
          value={draft.title}
          maxLength={128}
          onChange={(e) => onChange({ ...draft, title: e.target.value })}
          placeholder="Название (пусто — начало текста)"
          aria-label="Название шаблона"
          className={`${input} h-8 flex-1`}
        />
        <select
          value={draft.category_id}
          onChange={(e) => onChange({ ...draft, category_id: Number(e.target.value) })}
          aria-label="Категория шаблона"
          className={`${input} h-8`}
        >
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.personal ? "Мои · " : ""}
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <textarea
        value={draft.text}
        maxLength={4000}
        rows={5}
        onChange={(e) => onChange({ ...draft, text: e.target.value })}
        onKeyDown={(e) => e.key === "Escape" && onCancel()}
        placeholder="Текст ответа клиенту"
        aria-label="Текст шаблона"
        className={`${input} resize-y py-1.5 leading-[19px]`}
      />
      <div className="flex gap-1.5">
        <button
          type="submit"
          disabled={!draft.text.trim()}
          className="h-8 rounded-[var(--radius-ui)] bg-accent px-3 text-white disabled:opacity-50"
        >
          {draft.id === null ? "Добавить" : "Сохранить"}
        </button>
        <button type="button" onClick={onCancel} className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-fg-muted hover:bg-bg-hover">
          Отмена
        </button>
      </div>
    </form>
  );
}
